import { useCallback, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import type { BatteryProposalDto } from "~/api/dto/battery-proposal.dto"
import { savingsAreShown } from "~/api/hooks/useBatteryProposalArbitrage"
import useBatteryProposalFinancialSummary from "~/api/hooks/useBatteryProposalFinancialSummary"
import EnvVars from "~/env-vars"
import { FinanceType } from "~/utils/finance/constants"

import { isBatteryProposalReadOnly } from "./battery-proposal-read-only"
import {
  BATTERY_UPDATES_SPEC,
  batteryPaymentFix,
  batterySavingsFix,
  batterySavingsInFlight,
  batteryUpdateActionLabel,
  batteryUpdatesNeeded,
  batteryUpdateStaleLabel,
  type BatteryUpdateKey,
  type BatteryUpdateReads,
} from "./battery-updates"
import { useBatteryFinancingLock } from "./financing-status/concert/use-battery-financing-lock"
import { LIGHTREACH_BATTERY_FINANCE_TYPE } from "./lightreach-battery"
import {
  type BatterySavingsOutcome,
  useBatteryUpdatesRunner,
} from "./use-battery-updates-runner"
import { updatesBlockedBy } from "../ProposalPresentation/updates/proposal-updates"
import { UpdatesPanel } from "../ProposalPresentation/updates/UpdatesPanel"
import {
  type UpdatesRailState,
  useUpdatesPanelState,
} from "../ProposalPresentation/updates/use-updates-panel-state"

export const BATTERY_UPDATES_INTRO = `Your edits changed this proposal. Update it before you present or share.`
export const BATTERY_UPDATES_LOCK_UNAVAILABLE = `Couldn't check whether financing locks this proposal's pricing.`

export type BatteryUpdates = {
  readonly rail: UpdatesRailState
  /** The panel and its minimized tab, for the presentation's chrome. */
  readonly panel: JSX.Element | null
}

type SavingsTracking = {
  readonly proposalId: string
  /**
   * `running`: the panel waits on the run. `ended`: it stopped waiting on a
   * run that ended, and the Proposal read may not render that yet. `shown`:
   * the read renders the end. `queued`: it stopped waiting on a run the read
   * still has in the queue.
   */
  readonly phase: `ended` | `queued` | `running` | `shown`
}

/**
 * A savings run the panel started keeps its row while it runs and after the
 * panel stops waiting, so the seller sees why: it failed, needs an input, or
 * is still in the queue. Then current savings, hidden savings, or the next
 * lifecycle step let it go: another queued run after an end (the gateway
 * queues one after a Setup edit) isn't the panel's, and a queued run that
 * ends is the Setup rail's to report.
 */
const useSavingsTracking = (
  proposal: BatteryProposalDto,
): {
  readonly tracked: boolean
  readonly onStarted: () => void
  readonly onEnded: (outcome: BatterySavingsOutcome) => void
} => {
  const proposalId = proposal.id
  const [tracking, setTracking] = useState<SavingsTracking | null>(null)
  const current = tracking?.proposalId === proposalId ? tracking : null
  const savings = proposal.arbitrage
  const inFlight = batterySavingsInFlight({ savings })
  if (current !== null && current.phase !== `running`) {
    const release =
      savings.status === `ready` ||
      !savingsAreShown(savings.visibility) ||
      (current.phase === `queued` && !inFlight) ||
      (current.phase === `shown` && inFlight)
    if (release) {
      setTracking(null)
    } else if (current.phase === `ended` && !inFlight) {
      setTracking({ proposalId, phase: `shown` })
    }
  }
  const onStarted = useCallback(
    () => setTracking({ proposalId, phase: `running` }),
    [proposalId],
  )
  const onEnded = useCallback(
    (outcome: BatterySavingsOutcome) =>
      setTracking((was) => (was === null ? null : { ...was, phase: outcome })),
    [],
  )
  return { tracked: current !== null, onStarted, onEnded }
}

/**
 * The seller's Updates panel on a Battery-Only Proposal: it slides in while
 * pricing, a LightReach payment or savings are out of date, and away once
 * everything is current. A read-only viewer gets no panel or badges.
 *
 * It reads what the page already reads — the financial summary and the
 * Proposal's savings lifecycle — and the financing lock only while pricing
 * needs a refresh.
 */
export const useBatteryUpdates = (
  proposal: BatteryProposalDto,
): BatteryUpdates => {
  const proposalId = proposal.id
  const enabled = !isBatteryProposalReadOnly(proposal)
  // The same read the financial summary makes, so it costs nothing more.
  const summaryQuery = useBatteryProposalFinancialSummary(proposalId, {
    enabled: enabled && proposal.proposalCost != null,
  })
  const priceSetByOffer =
    proposal.financePlan?.financeType === LIGHTREACH_BATTERY_FINANCE_TYPE
  const savingsEnabled = EnvVars.BATTERY_SAVINGS_ENABLED
  const savingsTracking = useSavingsTracking(proposal)
  const savingsTracked = savingsTracking.tracked
  // Only a financed plan has a payment to quote.
  const financed =
    proposal.financePlan != null &&
    proposal.financePlan.financeType !== FinanceType.CASH
  const reads: BatteryUpdateReads = {
    pricing: summaryQuery.data,
    savings: proposal.arbitrage,
    priceSetByOffer,
    savingsEnabled,
    savingsTracked,
    // A failed check leaves old figures on screen; whether they're current
    // is unknown.
    unknown: new Set<BatteryUpdateKey>(
      summaryQuery.isError
        ? financed
          ? [`pricing`, `payment`]
          : [`pricing`]
        : [],
    ),
  }
  const needed = enabled ? batteryUpdatesNeeded(reads) : []

  const runner = useBatteryUpdatesRunner({
    proposalId,
    settled: needed.length === 0,
    priceSetByOffer,
    savingsEnabled,
    onSavingsStarted: savingsTracking.onStarted,
    onSavingsEnded: savingsTracking.onEnded,
  })
  const panel = useUpdatesPanelState({
    spec: BATTERY_UPDATES_SPEC,
    needed,
    runner,
    enabled,
  })

  // Refresh Pricing is refused under an approved application's lock, and
  // waits while the lock is unknown; savings aren't locked, and a payment row
  // links out.
  const pricingNeeded = needed.includes(`pricing`)
  const lock = useBatteryFinancingLock(proposalId, {
    enabled: panel.active && pricingNeeded,
  })
  const locked = (key: BatteryUpdateKey): boolean => {
    switch (key) {
      case `pricing`:
        return lock.locked || lock.pending || !lock.known || lock.unavailable
      case `savings`:
        return batterySavingsInFlight(reads)
      case `payment`:
        return false
    }
  }
  const queryClient = useQueryClient()
  const unknown = panel.active
    ? updatesBlockedBy({
        spec: BATTERY_UPDATES_SPEC,
        key: `all`,
        unknown: reads.unknown,
      })
    : []

  return {
    rail: panel.rail,
    panel: enabled ? (
      <UpdatesPanel
        spec={BATTERY_UPDATES_SPEC}
        intro={BATTERY_UPDATES_INTRO}
        open={panel.open}
        minimized={panel.minimized}
        rows={panel.rows}
        needed={needed}
        runner={runner}
        staleLabel={(key) => batteryUpdateStaleLabel(key, reads)}
        actionLabel={(key) => batteryUpdateActionLabel(key, reads)}
        fixElsewhere={(key) =>
          key === `payment`
            ? batteryPaymentFix(proposalId, priceSetByOffer)
            : key === `savings`
              ? batterySavingsFix(proposalId, reads)
              : null
        }
        locked={locked}
        // The notes explain a disabled Refresh, so they show only beside one.
        lock={lock.locked && pricingNeeded ? lock.reason : null}
        lockUnavailable={
          lock.unavailable && pricingNeeded
            ? BATTERY_UPDATES_LOCK_UNAVAILABLE
            : null
        }
        onRetryLock={() => {
          for (const queryKey of [
            queryKeys.batteryProposal.financing(proposalId),
            queryKeys.batteryProposal.concertApplication(proposalId),
          ]) {
            void queryClient.refetchQueries({
              queryKey,
              exact: true,
              type: `active`,
            })
          }
        }}
        unknown={unknown}
        onRetryUnknown={() =>
          void queryClient.refetchQueries({
            queryKey: queryKeys.batteryProposal.financialSummary(proposalId),
            exact: true,
          })
        }
        onMinimize={panel.onMinimize}
        onRestore={panel.onRestore}
      />
    ) : null,
  }
}
